import React, { useCallback, useEffect, useState } from 'react';
import { Card, Dimmer, Loader, Message } from 'semantic-ui-react';
import { ITEMS_PER_PAGE } from 'constants/constants';
import { getMessageFromAxiosError } from 'modules/message-factory/message-factory';
import { getAll, update } from 'services/users.service';
import UsersTable from 'components/UsersTable/UsersTable';
import { useAlert } from 'context/AlertProvider';
import SearchField from 'components/Field/SearchField/SearchField';
import Portal from 'components/Portal/Portal';
import { useLocalFilteredResults } from 'hooks/useLocalFilteredResults';
import { isPending } from 'redux/statuses';
import { useWarehouses } from 'hooks/redux/useWarehouses';
import { useGroups } from 'hooks/redux/useGroups';
import { useMarketplaces } from 'hooks/redux/useMarketplaces';
import useFiltersReducer, { loadMoreAction } from 'hooks/useFiltersReducer';

export default function UsersPage() {
  const alert = useAlert();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [hasNextPage, setHasNextPage] = useState(true);
  const [state, dispatch] = useFiltersReducer({
    page: 1,
  });
  const { warehouses, warehousesStatus } = useWarehouses();
  const { groups } = useGroups();
  const { marketplaces, marketplacesStatus } = useMarketplaces();

  const fetchUsers = useCallback(
    async (state) => {
      try {
        setLoading(true);
        const { data: users } = await getAll({
          page: state.page,
          itemsPerPage: ITEMS_PER_PAGE,
        });
        setHasNextPage(users?.length === ITEMS_PER_PAGE);
        setUsers((prev) => (state.page > 1 ? [...prev, ...users] : users));
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [alert],
  );

  const filteredUsers = useLocalFilteredResults(users, query);

  useEffect(() => {
    (async () => {
      await fetchUsers(state);
    })();
  }, [state, fetchUsers]);

  const updateUser = useCallback(
    async (data) => {
      try {
        setLoading(true);
        await update(data);
        await fetchUsers(state);
      } catch (error) {
        alert.current.error(getMessageFromAxiosError(error));
      } finally {
        setLoading(false);
      }
    },
    [alert, fetchUsers, state],
  );

  return (
    <>
      <Card.Header>
        <Portal.In target="page-header-right">
          <SearchField
            value={query}
            placeholder="Buscar…"
            onChange={setQuery}
          />
        </Portal.In>
      </Card.Header>
      <Card.Content>
        <Dimmer active={loading && state.page === 1} inverted>
          <Loader>Recuperando usuarios…</Loader>
        </Dimmer>
        <Dimmer active={isPending(warehousesStatus)} inverted>
          <Loader>Recuperando almacenes…</Loader>
        </Dimmer>
        <Dimmer active={isPending(marketplacesStatus)} inverted>
          <Loader>Recuperando marketplaces…</Loader>
        </Dimmer>
        {!!filteredUsers.length ? (
          <>
            <UsersTable
              loading={loading}
              warehouses={warehouses}
              marketplaces={marketplaces}
              users={filteredUsers}
              groups={groups}
              onUpdateUser={updateUser}
              onLoadMoreItems={(skip) => dispatch(loadMoreAction())}
              hasNextPage={hasNextPage}
            />
            <small>{users.length} items</small>
          </>
        ) : (
          <Message info>
            No hay usuarios que mostrar para los criterios de filtrado
            seleccionado…
          </Message>
        )}
      </Card.Content>
    </>
  );
}
